import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Back-in-stock form on the product page (blocks/yomera-back-in-stock.liquid).
 * Liquid decides visibility from the selected variant. On variant change this takes the
 * `hidden` state and the hidden product fields from the re-rendered section, leaving the
 * email field (and anything typed into it) untouched.
 *
 * @typedef {{ fields?: HTMLElement }} Refs
 * @extends {Component<Refs>}
 */
class YomeraBackInStock extends Component {
  /** @type {Element | null} */
  #scope = null;

  connectedCallback() {
    super.connectedCallback();
    this.#scope = this.closest('.shopify-section, dialog');
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#scope?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#scope = null;
  }

  /**
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = async (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card') || !event.promise) return;

    let result;
    try {
      result = await event.promise;
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[yomera-back-in-stock] Event promise rejected:', error);
      return;
    }

    const html = result?.detail?.html;
    if (!(html instanceof Document) || !this.isConnected) return;

    const blockId = this.dataset.blockId;
    if (!blockId) return;
    const next = html.querySelector(`yomera-back-in-stock[data-block-id="${CSS.escape(blockId)}"]`);
    if (!next) return;

    const nextFields = next.querySelector('[ref="fields"]');
    if (nextFields && this.refs.fields) {
      this.refs.fields.replaceChildren(...[...nextFields.childNodes].map((node) => document.importNode(node, true)));
    }
    this.hidden = next.hasAttribute('hidden');
  };
}

if (!customElements.get('yomera-back-in-stock')) {
  customElements.define('yomera-back-in-stock', YomeraBackInStock);
}
